/**
 * Hook to get a scoped Typesense API key for a specific collection.
 * Fetches collection-specific API keys from the backend.
 * Use with the singleton typesense client by passing the key via 'x-typesense-api-key' header.
 *
 * TODO: This is a general-purpose hook used across the application (main search,
 * actions search, legacy search, and filters). Consider moving to a more appropriate
 * location like /api/typesense/hooks/useTypesenseAPIKey.ts in a future PR.
 */

import { useMemo } from 'react';

import { useGetTypeSenseAPIKeys } from '@/api/REST/typeSenseAPIKeys/useGetTypeSenseAPIKeys';

/**
 * Valid Typesense API key names
 * Must match the keys available from the backend API
 */
export type TypesenseAPIKeyName =
  | 'projects'
  | 'projectcategories'
  | 'projecttypes'
  | 'customers'
  | 'invoices'
  | 'employees'
  | 'contacts'
  | 'actions'
  | 'departments'
  | 'employeetypes'
  | 'legalentities'
  | 'partners'
  | 'managers'
  | 'projectmanagers'
  | 'accountmanagers'
  | 'dashboards';

/**
 * Returns a scoped API key for the given collection.
 * Use this with the singleton typesense client from @/api/typesense/typesenseClient
 *
 * @param collectionName - Name of the Typesense collection (e.g., "projects")
 * @returns Object with API key and loading state
 */
export function useTypesenseAPIKey(collectionName: string) {
  const { data: apiKeys, isLoading, isError } = useGetTypeSenseAPIKeys();
  const apiKey = useMemo(() => {
    if (!apiKeys) {
      return null;
    }

    // Map collection names (case-insensitive) to API key collection types
    type KeyName = TypesenseAPIKeyName;

    const keyMap: Record<string, KeyName> = {
      projects: 'projects',
      customers: 'customers',
      customers_options: 'customers', // Options collections use same key
      categories_options: 'projects', // Assuming categories are part of projects scope
      projectcategories: 'projectcategories',
      projecttypes: 'projecttypes',
      projectmanagers: 'projectmanagers',
      invoices: 'invoices',
      employees: 'employees',
      contacts: 'contacts',
      actions: 'actions',
      departments: 'departments',
      employeetypes: 'employeetypes',
      legalentities: 'legalentities',
      partners: 'partners',
      managers: 'managers',
      accountmanagers: 'accountmanagers',
      dashboards: 'dashboards',
    };
    const normalizedName = collectionName.toLowerCase();
    const keyName = keyMap[normalizedName];

    if (!keyName) {
      return null;
    }

    return apiKeys[keyName]?.key || null;
  }, [apiKeys, collectionName]);

  return {
    apiKey,
    isLoading,
    isError: isError || (!apiKey && !isLoading),
  };
}
